import { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../../../components/Layout/Header";
import Footer from "../../../../components/Layout/Footer";
import Sidebar from "../../../../components/Layout/Sidebar";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import {
  getData,
  deleteData,
  getcountries,
} from "../../../../services/event/participants/participantSetupService";

const PAGE_SIZE = 10;

/* ── inline styles ─────────────────────────────────── */
const S = {
  page: {
    background: "#f4f6f9",
    minHeight: "100vh",
    fontFamily: "'DM Sans', sans-serif",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  titleSmall: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: "#6b7a8d",
    marginBottom: 2,
  },
  titleLarge: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a2332",
    marginTop: 10,
    fontFamily: "'DM Sans', sans-serif",
  },
  addBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "9px 20px",
    background: "#1a6b3c",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.18s",
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e3e8ef",
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(26,107,60,0.06)",
  },
  tableHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #e3e8ef",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
  },
  searchWrap: {
    position: "relative",
    width: 260,
  },
  searchIcon: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9aa5b4",
    fontSize: 14,
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "8px 12px 8px 34px",
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    fontSize: 13,
    outline: "none",
    fontFamily: "'DM Sans', sans-serif",
    color: "#1a2332",
    transition: "border-color 0.15s",
  },
  countBadge: {
    background: "#e8f5ee",
    color: "#1a6b3c",
    fontSize: 12,
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: 20,
  },
  editBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 12px",
    background: "#fff8e6",
    color: "#b07d1a",
    border: "1.5px solid #f0d080",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    marginRight: 6,
    transition: "all 0.15s",
    fontFamily: "'DM Sans', sans-serif",
  },
  deleteBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 12px",
    background: "#fff1f0",
    color: "#c0392b",
    border: "1.5px solid #f5c6c3",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s",
    fontFamily: "'DM Sans', sans-serif",
  },
  namePill: {
    fontWeight: 600,
    color: "#1a2332",
    fontSize: 13.5,
  },
  affiliationText: {
    color: "#4a5568",
    fontSize: 13,
  },
  receiptCode: {
    fontFamily: "monospace",
    fontSize: 12,
    background: "#f0f4f8",
    border: "1px solid #e3e8ef",
    padding: "2px 8px",
    borderRadius: 4,
    color: "#4a5568",
  },
  emptyState: {
    textAlign: "center",
    padding: "56px 20px",
    color: "#9aa5b4",
  },
  deleteModalIcon: {
    width: 52,
    height: 52,
    background: "#fff1f0",
    border: "1.5px solid #f5c6c3",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    fontSize: 22,
  },
};

const customTableStyles = {
  headRow: {
    style: {
      background: "#f8fafb",
      borderBottom: "2px solid #e3e8ef",
      minHeight: 44,
    },
  },
  headCells: {
    style: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: "#6b7a8d",
      fontFamily: "'DM Sans', sans-serif",
      paddingLeft: 16,
      paddingRight: 16,
    },
  },
  rows: {
    style: {
      fontSize: 13.5,
      fontFamily: "'DM Sans', sans-serif",
      borderBottom: "1px solid #f0f4f8",
      minHeight: 52,
      transition: "background 0.12s",
      "&:hover": {
        background: "#f5fbf7",
        cursor: "default",
      },
    },
  },
  cells: {
    style: {
      paddingLeft: 16,
      paddingRight: 16,
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid #e3e8ef",
      fontSize: 13,
      fontFamily: "'DM Sans', sans-serif",
      color: "#4a5568",
    },
  },
};

const ParticipantSetupList = () => {
  const navigate = useNavigate();
  const [dataList, setDataList] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [countriesByCode, setCountriesByCode] = useState({});

  const subDomain = localStorage.getItem("sub_domain") || "";
  const series = localStorage.getItem("series") || "";

  const fetchData = useCallback(async () => {
    try {
      const data = await getData();
      setDataList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await getcountries();
      const countryList = Array.isArray(response?.data) ? response.data : [];
      const nextCountryMap = countryList.reduce((acc, country) => {
        if (country?.code) {
          acc[country.code] = country.name;
        }
        return acc;
      }, {});
      setCountriesByCode(nextCountryMap);
    } catch (error) {
      console.error("Failed to fetch countries:", error);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchData(), fetchCountries()]);
      } catch (error) {
        console.error("Failed to load participant setup data:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [fetchCountries, fetchData]);

  const handleDelete = async () => {
    if (!selectedData) return;

    setDeletingId(selectedData.id);

    try {
      await deleteData(selectedData);
      setDataList((prev) => prev.filter((item) => item.id !== selectedData.id));
    } catch (error) {
      console.error("Failed to delete participant:", error);
    } finally {
      setShowDeleteConfirmModal(false);
      setDeletingId(null);
      setSelectedData(null);
    }
  };

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteConfirmModal(false);
    setSelectedData(null);
  }, []);

  const openDeleteConfirmModal = useCallback((data) => {
    setSelectedData(data);
    setShowDeleteConfirmModal(true);
  }, []);

  const handleAddParticipant = useCallback(() => {
    navigate("/participantSetup/create");
  }, [navigate]);
  

  const handleEditParticipant = useCallback((row) => {
    navigate(`/participantSetup/${row.id}`);
  }, [navigate]);

  const filteredData = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase();

    return dataList
      .filter((item) => item?.sub_domain === subDomain && item?.series === series)
      .filter((item) => {
        if (!normalizedSearchText) return true;

        const searchableText = `${item?.name || ""} ${item?.affiliation || ""} ${item?.country || ""}`.toLowerCase();
        return searchableText.includes(normalizedSearchText);
      })
      .sort((a, b) => new Date(b?.updatedAt || 0) - new Date(a?.updatedAt || 0));
  }, [dataList, searchText, subDomain, series]);

  const columns = useMemo(
    () => [
      {
        name: "#",
        selector: (_, index) => (currentPage - 1) * PAGE_SIZE + (index + 1),
        width: "60px",
        style: { color: "#9aa5b4", fontFamily: "monospace", fontSize: 12 },
      },
      {
        name: "Name",
        cell: (row) => (
          <div>
            <div style={S.namePill}>{row.name}</div>
            {row.country && (
              <div style={{ fontSize: 11, color: "#9aa5b4", marginTop: 2 }}>
                {countriesByCode[row.country] || ""}
              </div>
            )}
          </div>
        ),
        sortable: true,
        selector: (row) => row.name,
        grow: 1.5,
      },
      {
        name: "SubDomain & Series",
        cell: (row) => (
          <div>
            <div style={S.namePill}>{row.sub_domain}</div>
            {row.series && (
              <div style={{ fontSize: 11, color: "#9aa5b4", marginTop: 2 }}>
                {row.series || ""}
              </div>
            )}
          </div>
        ),
        sortable: true,
        selector: (row) => row.sub_domain,
        grow: 1.5,
      },
      {
        name: "Affiliation",
        cell: (row) => (
          <span style={S.affiliationText} title={row.affiliation}>
            {row.affiliation?.length > 40 ? `${row.affiliation.slice(0, 40)}…` : row.affiliation}
          </span>
        ),
        sortable: true,
        selector: (row) => row.affiliation,
        grow: 2,
      },
      {
        name: "Country",
        cell: (row) =>
          row.country ? (
            <span style={S.receiptCode}>{countriesByCode[row.country] || ""}</span>
          ) : (
            <span style={{ color: "#c8d0db", fontSize: 12 }}>—</span>
          ),
        sortable: true,
        selector: (row) => row.country,
      },
      {
        name: "Actions",
        cell: (row) => (
          <div style={{ display: "flex", gap: 0 }}>
            <button style={S.editBtn} onClick={() => handleEditParticipant(row)}>
              ✎ Edit
            </button>
            <button
              style={S.deleteBtn}
              onClick={() => openDeleteConfirmModal(row)}
              disabled={deletingId === row.id}
            >
              ✕ Delete
            </button>
          </div>
        ),
        width: "168px",
        right: true,
      },
    ],
    [countriesByCode, currentPage, deletingId, handleEditParticipant, openDeleteConfirmModal]
  );

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100" style={S.page}>
        <Header />
        <div className="container-fluid p-4">
          <div style={S.topBar}>
            <div style={S.titleBlock}>
              <div style={S.titleSmall}>Conference Program &rsaquo; Participant Setup</div>
              <h2 style={S.titleLarge}>Participant Setup</h2>
            </div>
            <button
              style={S.addBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#2d8a55";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(26,107,60,0.28)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#1a6b3c";
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "none";
              }}
              onClick={handleAddParticipant}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
              Add Participant
            </button>
          </div>

          <div style={S.card}>
            <div style={S.tableHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#1a2332" }}>
                  All Participants
                </span>
                {!loading && <span style={S.countBadge}>{filteredData.length}</span>}
              </div>
              <div style={S.searchWrap}>
                <span style={S.searchIcon}>⌕</span>
                <input
                  style={S.searchInput}
                  type="text"
                  placeholder="Search by name…"
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setCurrentPage(1);
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#1a6b3c")}
                  onBlur={(e) => (e.target.style.borderColor = "#e3e8ef")}
                />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              responsive
              striped={false}
              paginationPerPage={PAGE_SIZE}
              paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
              onChangePage={(page) => setCurrentPage(page)}
              progressPending={loading}
              progressComponent={<TableSkeleton />}
              noDataComponent={
                <div style={S.emptyState}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>No participants found</div>
                  <div style={{ fontSize: 13 }}>
                    {searchText ? "Try a different search term" : "Add your first participant to get started"}
                  </div>
                </div>
              }
              customStyles={customTableStyles}
            />
          </div>
        </div>
        <Footer />
      </div>

      <Modal
        show={showDeleteConfirmModal}
        onHide={handleCloseDeleteModal}
        centered
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        <Modal.Body style={{ padding: "32px 28px", textAlign: "center" }}>
          <div style={S.deleteModalIcon}>🗑</div>
          <h5 style={{ fontWeight: 700, color: "#1a2332", marginBottom: 8 }}>Delete Participant</h5>
          <p style={{ color: "#6b7a8d", fontSize: 14, marginBottom: 24 }}>
            Are you sure you want to delete <strong style={{ color: "#1a2332" }}>{selectedData?.name}</strong>?
            <br />
            This action cannot be undone.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button
              style={{
                padding: "9px 22px",
                border: "1.5px solid #e3e8ef",
                borderRadius: 8,
                background: "#fff",
                color: "#4a5568",
                fontWeight: 600,
                fontSize: 13.5,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
              onClick={handleCloseDeleteModal}
            >
              Cancel
            </button>
            <button
              style={{
                padding: "9px 22px",
                border: "none",
                borderRadius: 8,
                background: "#c0392b",
                color: "#fff",
                fontWeight: 600,
                fontSize: 13.5,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
              onClick={handleDelete}
            >
              Yes, Delete
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ParticipantSetupList;